/**
 * The page's side of the page-tools seam: what Emjae is told this screen can
 * do, and what actually runs when it asks.
 *
 * Emjae reaches the user's data through Core's server-side facades. A few
 * questions those cannot answer are about the SCREEN — the rows in the table
 * the user is looking at, under the filters they applied — and that lives in a
 * tab no backend can dial. So Eve parks the turn instead of executing, Core
 * sends the call down the stream the chat already listens on, and this module
 * is what runs it.
 *
 * Two lists, and the difference is the whole safety story. `getTools()` is
 * every tool registered on this document, writers included. `emjaeExposed` is
 * the subset that opted in, which `contract.ts` types so that only a `reads`
 * command may. This module never works from the first list alone: the
 * descriptors it sends are filtered by the second, and so is every call that
 * comes back. One filter is a feature; the second is the one that holds when
 * something upstream is wrong, because a call naming a writer is refused here
 * without the page ever seeing it.
 */
import {
  registerClientToolExecutor,
  registerPageToolsProvider,
  type ClientToolExecutor,
} from '@/components/CentralChat/clientToolBridge';
import type { PageToolDescriptor } from '@/components/CentralChat/types';
import { resolveModelContext } from './modelContext';

export type { PageToolDescriptor };

/**
 * What this document exposed, as descriptors. Read synchronously when a turn
 * is sent, so it cannot be a promise; empty until `installPageToolsForChat`
 * fills it, and empty forever in a browser with no WebMCP, which is the common
 * case and the reason this seam costs those browsers nothing.
 *
 * `input_schema` is passed along exactly as Chrome reported it and never
 * parsed. Core does not read it either; the model is its only consumer. See
 * the `inputSchema` note in `webmcp.d.ts` for why it is a string.
 */
let descriptors: readonly PageToolDescriptor[] = [];

/** Tools this document registered, narrowed to the ones that opted in to Emjae. */
async function exposedTools(exposed: readonly string[]): Promise<WebMCP.RegisteredTool[]> {
  const context = resolveModelContext();
  if (!context || exposed.length === 0) return [];
  const allowed = new Set(exposed);
  const tools = await context.getTools();
  return tools.filter((tool) => allowed.has(tool.name));
}

/**
 * The descriptors for one installation, in the order `getTools` returned them.
 *
 * `emjaeExposed` comes from the install outcome rather than from the catalog,
 * so this describes what actually registered in THIS document instead of what
 * a build believed would.
 */
export async function buildPageToolDescriptors(
  exposed: readonly string[],
): Promise<PageToolDescriptor[]> {
  const tools = await exposedTools(exposed);
  return tools.map((tool) => ({
    name: tool.name,
    description: tool.description ?? '',
    input_schema: tool.inputSchema ?? '',
  }));
}

/**
 * An executor for the exposed set, and nothing else.
 *
 * The name is checked against `exposed` before the tool is looked up. Not
 * redundant with the descriptor filter: the descriptors say what Emjae was
 * OFFERED, and this says what it may RUN. A call for a name that was never
 * offered — a model that invented one, a session replaying an older page's
 * tools, a catalog that changed under a parked turn — is refused here rather
 * than executed because it happens to be registered.
 */
export function createPageToolExecutor(exposed: readonly string[]): ClientToolExecutor {
  const allowed = new Set(exposed);
  return async (call, signal) => {
    if (!allowed.has(call.name)) {
      throw new Error(`${call.name} is not a page tool this page offers Emjae`);
    }
    const context = resolveModelContext();
    if (!context) throw new Error('This browser has no page tools');

    const tool = (await context.getTools()).find((entry) => entry.name === call.name);
    // Registered a moment ago and gone now: an HMR cycle, or a teardown that
    // raced the answer. An error is the honest report; the turn continues.
    if (!tool) throw new Error(`${call.name} is no longer registered on this page`);

    // Argument two is a REQUIRED JSON string in shipping Chrome. Passing the
    // object silently does not work.
    const result = await context.executeTool(tool, JSON.stringify(call.args ?? {}), { signal });

    // `null` means the tool navigated instead of answering. Returning it would
    // reach the model as an empty success, which reads as "the page has no
    // rows" rather than "the page never answered".
    if (result === null) {
      throw new Error(`${call.name} navigated instead of returning a result`);
    }
    return result;
  };
}

/**
 * What the current page offers Emjae, for the request being built right now.
 *
 * Returns a copy: the array crosses into the chat's request body, and a caller
 * that mutated it would change what every later turn reports.
 */
export function currentPageTools(): PageToolDescriptor[] {
  return [...descriptors];
}

/** Replaces the cached descriptors. `null` clears them, on teardown. */
export function setPageToolDescriptors(next: readonly PageToolDescriptor[] | null): void {
  descriptors = next ?? [];
}

/**
 * Joins this installation to the chat: what may be offered, and what may run.
 *
 * Both halves are registered from here rather than from the chat, because both
 * are answers only this lane has. The chat registers the third half, the
 * transport that posts a result back, because that needs the conversation and
 * the stream handler and this module has neither.
 *
 * Nothing is registered when the exposed set is empty. A page with no opted-in
 * tools should look exactly like a browser without WebMCP.
 */
export async function installPageToolsForChat(exposed: readonly string[]): Promise<number> {
  if (exposed.length === 0) return 0;
  const built = await buildPageToolDescriptors(exposed);
  if (built.length === 0) return 0;
  setPageToolDescriptors(built);
  registerPageToolsProvider(currentPageTools);
  registerClientToolExecutor(createPageToolExecutor(exposed));
  return built.length;
}

/** Detaches this lane from the chat, leaving the seam as it found it. */
export function uninstallPageToolsForChat(): void {
  setPageToolDescriptors(null);
  registerPageToolsProvider(null);
  registerClientToolExecutor(null);
}
